import React, { FunctionComponent, useState } from "react";
import { Box } from "@mui/material";
import { formatDateTime } from "src/utils/date/dateHelper";
import { McGeneralDiseaseItem, mcGeneralDiseaseService } from "src/api/medcards/mcGeneralDiseaseService";
import { useTranslate } from "src/locales";
import ByzDeleteConfirmDialog from "src/components/core-components/mui/ByzDeleteConfirmDialog";

const dateIcon = "/assets/icons/clarity_date-line.svg";
const noteIcon = "/assets/icons/material-symbols_note-outline.svg";
const editIcon = "/assets/icons/mdi_pencil-outline.svg";
const trashIcon = "/assets/icons/mynaui_trash.svg";

interface RareDiseaseCardProps {
  disease: string;
  operationDate: string;
  operator: string;
  note: string;
  id: number;
  onDelete?: (id: number) => void;
  onEdit?: (item: McGeneralDiseaseItem) => void;
  onDeleteSuccess?: () => void;
  onDeleteError?: () => void;
}

const InfoRow = ({ icon, label, value }: { icon: string; label: string; value: string }) => (
  <Box sx={{ alignSelf: "stretch", display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "center" }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
      <Box component="img" src={icon} alt="" sx={{ width: "16px", height: "16px", overflow: "hidden" }} />
      <Box sx={{ position: "relative", lineHeight: "150%", fontWeight: 600 }}>{label}:</Box>
      <Box sx={{ position: "relative", lineHeight: "150%", fontWeight: 500, color: "rgba(17, 24, 39, 0.7)" }}>{value}</Box>
    </Box>
  </Box>
);

const RareDiseaseCard: FunctionComponent<RareDiseaseCardProps> = ({
  disease,
  operationDate,
  operator,
  note,
  id,
  onDelete,
  onEdit,
  onDeleteSuccess,
  onDeleteError,
}) => {
  const { t } = useTranslate();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await mcGeneralDiseaseService.deleteGeneralDisease(id);
      onDelete?.(id);
      onDeleteSuccess?.();
      return true;
    } catch (error) {
      console.error("Error deleting rare disease:", error);
      onDeleteError?.();
      return false;
    }
  };

  return (
    <>
      <Box sx={{
        borderRadius: "6px",
        backgroundColor: "#fff",
        border: "1px solid #e5e7eb",
        boxSizing: "border-box",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        padding: "16px",
        gap: "12px",
        textAlign: "left",
        fontSize: "14px",
        color: "#111827",
        fontFamily: "Urbanist",
      }}>
        <Box sx={{ alignSelf: "stretch", display: "flex", flexDirection: "column", alignItems: "flex-start", fontSize: "16px" }}>
          <Box sx={{ alignSelf: "stretch", display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
            <Box sx={{ alignSelf: "stretch", display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
              <Box sx={{ alignSelf: "stretch", display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                <Box sx={{ alignSelf: "stretch", display: "flex", alignItems: "center", gap: "12px" }}>
                  <Box sx={{ flex: 1, position: "relative", lineHeight: "150%", fontWeight: 600 }}>
                    {disease}
                  </Box>
                  <Box
                    component="img"
                    src={trashIcon}
                    alt={t("rare-disease.delete")}
                    onClick={handleDeleteClick}
                    sx={{ width: "20px", height: "20px", overflow: "hidden", cursor: "pointer" }}
                  />
                  <Box
                    component="img"
                    src={editIcon}
                    alt={t("rare-disease.edit")}
                    onClick={() => onEdit?.({
                      id,
                      name: disease,
                      pdate: operationDate,
                      ctime: operationDate,
                      cuser: operator,
                      note,
                      privatenotes: note,
                    } as McGeneralDiseaseItem)}
                    sx={{ width: "20px", height: "20px", overflow: "hidden", cursor: "pointer" }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        <InfoRow icon={dateIcon} label={t("rare-disease.operation-date")} value={operationDate || ""} />
        <InfoRow icon={dateIcon} label={t("rare-disease.operation-user-short")} value={operator || ""} />
        <InfoRow icon={noteIcon} label={t("rare-disease.note-section")} value={note || ""} />
      </Box>

      <ByzDeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onDelete={handleDeleteConfirm}
        onSuccess={(success) => {
          if (success) {
            setDeleteDialogOpen(false);
          }
        }}
        title={t("rare-disease.delete-confirm-title")}
        content={t("rare-disease.delete-confirm-content")}
        successMessage={t("rare-disease.delete-success")}
        errorMessage={t("rare-disease.delete-error")}
        hideSnackbar={true}
      />
    </>
  );
};

interface RareDiseasesCardListProps {
  data?: McGeneralDiseaseItem[];
  onDelete?: (id: number) => void;
  onEdit?: (item: McGeneralDiseaseItem) => void;
  onDeleteSuccess?: () => void;
  onDeleteError?: () => void;
}

const RareDiseasesCardList: FunctionComponent<RareDiseasesCardListProps> = ({ data = [], onDelete, onEdit, onDeleteSuccess, onDeleteError }) => {
  const items: RareDiseaseCardProps[] = data.map((item) => ({
    disease: item.name || "",
    operationDate: formatDateTime(item.pdate || item.ctime, "dd.MM.yyyy HH:mm") || "",
    operator: item.cuser || "",
    note: item.note || item.privatenotes || "",
    id: item.id,
    onDelete,
    onEdit: () => onEdit?.(item),
    onDeleteSuccess,
    onDeleteError,
  }));

  if (items.length === 0) {
    return null;
  }

  return (
    <Box sx={{ alignSelf: "stretch", display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "10px" }}>
      {items.map((item, index) => (
        <RareDiseaseCard key={`${item.disease}-${item.operationDate}-${index}`} {...item} />
      ))}
    </Box>
  );
};

export default RareDiseasesCardList;

